import styled, { createGlobalStyle, keyframes } from 'styled-components';
import {
  colors,
  duration,
  fadeIn,
  fadeOut,
  easing,
  largeShadow,
  smallShadow,
  spacing,
  subtleShadow,
  zIndex,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';

export const SLIDEOVER_EXIT_ANIMATION_DURATION_IN_MS = 300;
export const SLIDE_OVER_WIDTH = '400px';
export const SLIDE_OVER_SPACING_PLUS_WIDTH = `${pxToRem(448)}`; // Base plus spacing on sides
const slideOverDataIsShown = 'data-is-shown';
const slideOverDataIsAnimationExiting = 'data-animation-exiting';

export const SlideOverContentWrapper = styled.div<{ $isExpanded: boolean; $bodyBgColor?: string }>`
  display: flex;
  align-self: center;
  flex-direction: column;
  position: relative;
  background: ${({ $bodyBgColor }) => $bodyBgColor ?? colors.white};
  margin: ${spacing.plusHalf};
  ${largeShadow};
  border-radius: ${spacing.threeQuarter};
  height: calc(100% - ${spacing.plusHalf});
  overflow: hidden;
`;

const slideRightAndFadeIn = keyframes({
  '0%': { opacity: 0, transform: 'translateX(100px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

const slideLeftAndFadeOut = keyframes({
  '0%': { opacity: 1, transform: 'translateX(0)' },
  '100%': { opacity: 0, transform: 'translateX(100px)' },
});

export const SlideOverWrapper = styled.div<{ $isExpanded: boolean }>`
  animation-duration: ${duration.slideover};
  animation-fill-mode: forwards;

  &[${slideOverDataIsShown}='true'] {
    animation-name: ${slideRightAndFadeIn};
  }
  &[${slideOverDataIsAnimationExiting}='true'] {
    animation-name: ${slideLeftAndFadeOut};
  }

  animation-timing-function: ${easing.primary};
  width: ${({ $isExpanded }) => ($isExpanded ? SLIDE_OVER_SPACING_PLUS_WIDTH : '0px')};
  position: fixed;
  right: 0;
  top: 0;
  background: none;
  z-index: ${zIndex.slideOver};
  height: calc(100% - ${spacing.plusHalf});
`;

export const SlideOverOverlay = styled.dialog`
  animation-duration: ${SLIDEOVER_EXIT_ANIMATION_DURATION_IN_MS}ms;
  animation-fill-mode: forwards;

  &[${slideOverDataIsShown}='true'] {
    animation-name: ${fadeIn};
  }
  &[${slideOverDataIsAnimationExiting}='true'] {
    animation-name: ${fadeOut};
  }

  animation-timing-function: ${easing.primary};

  position: fixed;
  background: ${colors.overlay};
  top: 0;
  width: 100%;
  height: 100%;
  border: none;
  z-index: ${zIndex.slideOverOverlay};
`;

// prevents scroll on body when slide over has overlay
export const OverlayBodyOverrides = createGlobalStyle`
  body {
    position: fixed;
    width: 100%;
    height: 100%;
  }
`;

export const SlideOverContent = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  padding: ${spacing.plusHalf};
  background-color: ${colors.backgroundSecondary};
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const SlideOverHeader = styled.div<{ $isScrolled: boolean }>`
  padding: ${spacing.base} ${spacing.plusHalf};
  display: flex;
  ${({ $isScrolled }) => ($isScrolled ? smallShadow : subtleShadow)};
  position: relative;
  justify-content: space-between;
  align-items: center;
  z-index: 2;
  background-color: ${colors.white};
`;

export const SlideOverFooter = styled.div`
  background: ${colors.white};
  padding: ${spacing.base} ${spacing.plusHalf};
  border-top: 1px solid ${colors.borderPrimarySubtle};
  position: relative;
  z-index: 2;
`;

export const SlideOverCloseBtn = styled.div`
  display: inline-block;
  align-self: self-start;
`;
